import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Phone, Menu, X } from "lucide-react";
import Logo from "@/components/Logo";
import { SITE } from "@/lib/site";
import { scrollToId } from "@/lib/scroll";

const LINKS = [
  { id: "about", label: "About Us" },
  { id: "services", label: "Products & Services" },
  { id: "serve", label: "Who We Serve" },
  { id: "contact", label: "Contact" },
];

export const Nav = ({ variant = "home" }) => {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const go = (e, id) => {
    e.preventDefault();
    setOpen(false);
    if (location.pathname !== "/") {
      window.location.href = `/#${id}`;
      return;
    }
    scrollToId(id);
  };

  return (
    <header
      data-testid="site-header"
      className="fixed top-0 inset-x-0 z-40 bg-white/70 backdrop-blur-xl border-b border-slate-200/60"
    >
      <div className="max-w-[1240px] mx-auto px-5 md:px-8 h-[72px] flex items-center justify-between">
        <Link to="/" data-testid="nav-home-link" aria-label="Eli Capital Investments — home" className="shrink-0">
          <Logo />
        </Link>

        {variant === "home" && (
          <nav className="hidden lg:flex items-center gap-9" aria-label="Primary">
            {LINKS.map((l) => (
              <a
                key={l.id}
                href={`/#${l.id}`}
                data-testid={`nav-${l.id}-link`}
                onClick={(e) => go(e, l.id)}
                className="text-[14px] font-medium text-slate-600 hover:text-ink transition-colors duration-200 relative group"
              >
                {l.label}
                <span className="absolute left-0 -bottom-1.5 h-[2px] w-0 bg-accent transition-all duration-300 group-hover:w-full" />
              </a>
            ))}
          </nav>
        )}

        <div className="flex items-center gap-3">
          <a
            href={SITE.phoneHref}
            data-testid="nav-call-button"
            className="hidden sm:inline-flex items-center gap-2 rounded-full bg-accent hover:bg-accentDeep text-white text-[14px] font-semibold px-5 py-2.5 transition-colors duration-200"
          >
            <Phone size={15} strokeWidth={2.2} />
            {SITE.phoneDisplay}
          </a>
          {variant === "home" && (
            <button
              type="button"
              data-testid="nav-mobile-toggle"
              aria-label={open ? "Close menu" : "Open menu"}
              onClick={() => setOpen((v) => !v)}
              className="lg:hidden inline-flex items-center justify-center w-10 h-10 rounded-full border border-slate-200 text-ink"
            >
              {open ? <X size={18} /> : <Menu size={18} />}
            </button>
          )}
        </div>
      </div>

      {open && variant === "home" && (
        <nav
          data-testid="nav-mobile-panel"
          className="lg:hidden border-t border-slate-200/60 bg-white/95 backdrop-blur-xl px-5 py-5 flex flex-col gap-4"
          aria-label="Mobile"
        >
          {LINKS.map((l) => (
            <a
              key={l.id}
              href={`/#${l.id}`}
              data-testid={`nav-mobile-${l.id}-link`}
              onClick={(e) => go(e, l.id)}
              className="font-display font-bold text-xl text-ink"
            >
              {l.label}
            </a>
          ))}
          <a
            href={SITE.phoneHref}
            data-testid="nav-mobile-call-button"
            className="mt-1 inline-flex items-center justify-center gap-2 rounded-full bg-accent text-white font-semibold px-5 py-3"
          >
            <Phone size={16} strokeWidth={2.2} />
            Call {SITE.phoneDisplay}
          </a>
        </nav>
      )}
    </header>
  );
};

export default Nav;
